import { useCallback } from 'react';
import { capitalize } from 'lodash/string';
import {
  useFilterSearchParamWithFF,
  useFilterSearchParam,
} from 'common/hooks/useFilterSearchParam';
import { useCurrentPayerId } from 'common/contexts/CurrentProviderContext';
import { useFind } from 'src/api/APIHooks';
import { useAssigneeOptions } from 'common/Workqueue/hooks/useAssigneeOptions';
import { REQUEST_TYPES } from '../constants';
import useFeatureFlag from '../../../common/hooks/useFeatureFlag';

export const useEnrollmentFilters = () => {
  const currentPayerId = useCurrentPayerId();
  const pays6096UpdateEWQDesign = useFeatureFlag('pays-6096-update-ewq-design');

  const { isLoading: isSccPlantDataLoading, data: sccPlanData } = useFind(
    'plans',
    {
      payer: currentPayerId,
      plan_type: 'social',
      enrollment_required: true,
    },
    {
      queryConfig: {
        enabled: !!currentPayerId,
        placeholderData: undefined,
      },
    },
  );
  const sccPlans = sccPlanData?.data.data ?? [];
  const sccPlanOptions = sccPlans?.map((plan) => ({
    label: plan.name,
    value: plan.id,
  }));

  const requiredHealthPlanIds = sccPlans?.map((plan) => (
    plan.required_health_plans?.map((required_plan) => required_plan.id)
  )).filter((value) => value !== undefined);
  const { isLoading: isRequiredHealthPlanDataLoading, data: insurancePlanData } = useFind(
    'plans',
    {
      id: requiredHealthPlanIds.join(','),
    },
    {
      queryConfig: {
        enabled: requiredHealthPlanIds?.length > 0 && pays6096UpdateEWQDesign,
        placeholderData: undefined,
      },
    },
  );
  const insurancePlans = insurancePlanData?.data.data ?? [];
  const insurancePlanOptions = insurancePlans.map((plan) => ({
    label: plan.name,
    value: plan.id,
  }));

  const { isLoading: isProviderDataLoading, data: providerData } = useFind(
    'providers',
    {
      'requester.enrollment_request.plan.payer': currentPayerId,
    },
    {
      queryConfig: {
        enabled: !!currentPayerId && pays6096UpdateEWQDesign,
        placeholderData: undefined,
      },
    },
  );
  const sendingProviders = providerData?.data.data ?? [];
  const sendingProviderOptions = sendingProviders.map((org) => ({
    label: org.name,
    value: org.id,
  }));

  const assigneeOptions = useAssigneeOptions('enrollments', true, true);

  const [requestType, setRequestType] = useFilterSearchParamWithFF(
    'request_type',
    undefined,
    'pays-6096-update-ewq-design',
  );
  const [sccPlan, setSccPlan] = useFilterSearchParamWithFF(
    'plan',
    undefined,
    'pays-6096-update-ewq-design',
  );
  const [daysSinceLastUpdated, setDaysSinceLastUpdated] = useFilterSearchParamWithFF(
    'days_since_last_updated',
    undefined,
    'pays-6096-update-ewq-design',
  );
  const [clientName, setClientName] = useFilterSearchParamWithFF(
    'client_name',
    undefined,
    'pays-6096-update-ewq-design',
  );
  const [requestShortId, setRequestShortId] = useFilterSearchParamWithFF(
    'request_short_id',
    undefined,
    'pays-6096-update-ewq-design',
  );
  const [insurancePlan, setInsurancePlan] = useFilterSearchParamWithFF(
    'insurance_plan',
    undefined,
    'pays-6096-update-ewq-design',
  );
  const [sendingProvider, setSendingProvider] = useFilterSearchParamWithFF(
    'sending_provider',
    undefined,
    'pays-6096-update-ewq-design',
  );
  const [createdAfter, setCreatedAfter] = useFilterSearchParamWithFF(
    'created_after',
    undefined,
    'pays-6096-update-ewq-design',
  );
  const [createdBefore, setCreatedBefore] = useFilterSearchParamWithFF(
    'created_before',
    undefined,
    'pays-6096-update-ewq-design',
  );
  const [insuranceMemberId, setInsuranceMemberId] = useFilterSearchParam(
    'insurance.primary_health_insurance.external_member_id_like',
  );
  const [urgency, setUrgency] = useFilterSearchParamWithFF(
    'urgency',
    undefined,
    'pays-enrollment-mark-as-urgent',
  );
  const [assignedTo, setAssignedTo] = useFilterSearchParam('assigned_to');

  const isFiltered = !!requestType || !!sccPlan || !!daysSinceLastUpdated || !!clientName ||
                    !!requestShortId || !!clientName || !!insurancePlan ||
                    !!sendingProvider || !!createdAfter || !!createdBefore ||
                    !!insuranceMemberId || !!assignedTo || !!urgency;

  const resetFilters = useCallback(() => {
    setRequestShortId(undefined);
    setRequestType(undefined);
    setAssignedTo(undefined);
    setClientName(undefined);
    setInsuranceMemberId(undefined);
    setSccPlan(undefined);
    setDaysSinceLastUpdated(undefined);
    setInsurancePlan(undefined);
    setSendingProvider(undefined);
    setCreatedAfter(undefined);
    setCreatedBefore(undefined);
    setUrgency(undefined);
  }, [setRequestShortId,
    setRequestType,
    setAssignedTo,
    setClientName,
    setInsuranceMemberId,
    setSccPlan,
    setDaysSinceLastUpdated,
    setInsurancePlan,
    setSendingProvider,
    setCreatedAfter,
    setCreatedBefore,
    setUrgency,
  ]);

  const daysSinceLastUpdatedOptions = [
    {
      label: 'Today',
      value: '1',
    },
    {
      label: 'Last 7 days',
      value: '7',
    },
    {
      label: 'Last 14 days',
      value: '14',
    },
    {
      label: 'Last 30 days',
      value: '30',
    },
    {
      label: 'Last 60 days',
      value: '60',
    },
  ];

  return {
    filterValues: {
      requestType,
      assignedTo,
      sccPlan,
      daysSinceLastUpdated,
      requestShortId,
      clientName,
      insuranceMemberId,
      insurancePlan,
      sendingProvider,
      createdAfter,
      createdBefore,
      urgency,
    },
    filterOptions: {
      requestType: Object.values(REQUEST_TYPES).map((type) => (
        { value: type, label: capitalize(type) }
      )),
      assignedTo: assigneeOptions,
      sccPlan: sccPlanOptions,
      daysSinceLastUpdated: daysSinceLastUpdatedOptions,
      insurancePlan: insurancePlanOptions,
      sendingProvider: sendingProviderOptions,
      urgency: [
        {
          label: 'Urgent',
          value: 'true',
        },
        {
          label: 'Not Urgent',
          value: 'false',
        },
      ],
    },
    isFiltered,
    isFilterDataLoading: (isSccPlantDataLoading || isRequiredHealthPlanDataLoading || isProviderDataLoading),
    resetFilters,
    setRequestShortIdFilter: setRequestShortId,
    setRequestTypeFilter: setRequestType,
    setAssignedToFilter: setAssignedTo,
    setClientNameFilter: setClientName,
    setInsuranceMemberIdFilter: setInsuranceMemberId,
    setSccPlanFilter: setSccPlan,
    setDaysSinceLastUpdatedFilter: setDaysSinceLastUpdated,
    setInsurancePlanFilter: setInsurancePlan,
    setSendingProviderFilter: setSendingProvider,
    setCreatedAfterFilter: setCreatedAfter,
    setCreatedBeforeFilter: setCreatedBefore,
    setUrgencyFilter: setUrgency,
  };
};
